import { Button, CircularProgress, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { withClarityTheme } from '@verifime/styles';

export type RequestReportButtonProps = {
  /**
   * Callback invoked when the button is clicked.
   */
  onClick: () => void;
  /**
   * Shows a spinner in place of the icon and disables the button while a
   * report request is in flight.
   */
  loading?: boolean;
  /**
   * Disables the button (e.g. when a report cannot be requested yet).
   */
  disabled?: boolean;
  /**
   * Tooltip text shown when the button is disabled. Only rendered while
   * `disabled` is true.
   */
  disabledTooltip?: string;
};

const RequestReportButtonComponent = ({
  onClick,
  loading = false,
  disabled = false,
  disabledTooltip,
}: Readonly<RequestReportButtonProps>) => {
  const theme = useTheme();
  const isDisabled = disabled || loading;

  const button = (
    <Button
      variant="outlined"
      onClick={onClick}
      disabled={isDisabled}
      data-action="request-report"
      data-testid="request-report-button"
      startIcon={
        loading ? (
          <CircularProgress size={theme.semantic.fontSize.base} color="inherit" />
        ) : (
          <AssessmentOutlinedIcon />
        )
      }
    >
      REQUEST REPORT
    </Button>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip}>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};

export const RequestReportButton = withClarityTheme(RequestReportButtonComponent);
